import { KnowledgeCardSchema } from './schema';
import { stage2AllCards } from './stage2-knowledgeCards';
import { stage3AllCards } from './stage3-knowledgeCards';

/**
 * 知识卡片数据
 * 新生儿期 - 喂养模块 - 关卡1：宝宝是不是饿了？
 */
export const feedingLevel1Cards: KnowledgeCardSchema[] = [
  {
    order: 1,
    title: '不要只等宝宝哭了才喂',
    oneLineSummary: '哭往往是较晚的饥饿信号，宝宝可能在哭之前已经发出提醒。',
    detailedExplanation:
      '新生儿可能会通过转头寻找、张嘴、舔嘴唇、吸吮手指或身体扭动等方式表达饥饿。如果总是等到大哭后再喂，宝宝可能更难安抚，也更难顺利含接。',
    commonMisconception: '认为"宝宝哭了才是真的饿"。',
    sourceReference: 'AAP 第7版 第4章',
    reviewStatus: 'draft',
  },
  {
    order: 2,
    title: '喂养不是只看时间表',
    oneLineSummary: '时间可以作为参考，但不能替代对宝宝状态的观察。',
    detailedExplanation:
      '不同宝宝的喂养节奏会有差异。照护者可以参考喂养间隔，但更重要的是观察宝宝是否有饥饿信号、吃奶表现、尿便情况和体重变化。',
    commonMisconception: '严格按固定小时数喂养，忽视宝宝的实际需求。',
    sourceReference: 'AAP 第7版 第4章',
    reviewStatus: 'draft',
  },
  {
    order: 3,
    title: '需要关注异常信号',
    oneLineSummary: '如果宝宝吃奶明显变少、精神差或伴随异常表现，需要及时寻求专业帮助。',
    detailedExplanation:
      '本产品提供家庭学习建议，不能替代医生判断。涉及持续拒奶、精神反应差、明显脱水表现、发热等情况，应及时咨询医生或就医。',
    commonMisconception: '认为所有喂养问题都可以自己解决。',
    sourceReference: 'AAP 第7版 第3章',
    reviewStatus: 'draft',
  },
  {
    order: 4,
    title: '配方奶粉冲调的正确方法',
    oneLineSummary: '严格按照罐身标注的冲调比例（通常1平勺配30ml水），用约40-50°C温开水冲调，冲好后手腕内侧试温。',
    detailedExplanation:
      '配方奶粉的冲调必须精确：①洗手后，先将水烧开冷却至约 40-50°C（WHO 建议用 ≥70°C 水冲调以杀灭奶粉中可能存在的致病菌，然后冷却至适口温度）；②先在奶瓶中加入所需水量，再加入对应平勺的奶粉——不可先放粉后加水，否则比例会改变；③用罐内量勺取奶粉，刮平但不要压实；④盖紧奶瓶，水平搓滚或上下摇晃使奶粉溶解（勿剧烈摇晃以免产生过多气泡）。每次即冲即饮，吃剩的奶应在 1 小时后丢弃，不可留到下一顿。',
    commonMisconception: '随意增减奶粉量"让宝宝吃得更营养"或冲稀一点"不上火"。',
    sourceReference: 'WHO《婴儿配方奶粉安全制备指南》；AAP 第7版 第3章',
    reviewStatus: 'draft',
  },
];

export const allKnowledgeCards: Record<string, KnowledgeCardSchema[]> = {
  ...stage2AllCards,
  ...stage3AllCards,
  'newborn-feeding-1': feedingLevel1Cards,
  'newborn-feeding-2': [
    {
      order: 1,
      title: '什么时候需要拍嗝',
      oneLineSummary: '喂奶过程中和喂完后都可以拍嗝，帮助宝宝排出吞入的空气。',
      detailedExplanation:
        '新生儿在吃奶时容易吞入空气，拍嗝可以帮助排气，减少溢奶和不适。可以在喂奶中途换边时拍一次，喂完后再拍一次。',
      commonMisconception: '认为拍嗝没用，或者只要拍出嗝就万事大吉。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '吐奶不等于生病',
      oneLineSummary: '少量吐奶（溢奶）在新生儿期非常常见，关键是学会区分正常溢奶和需要就医的信号。',
      detailedExplanation:
        '新生儿胃容量小、贲门（胃和食道连接处）较松弛，容易出现溢奶。正常溢奶量少、宝宝精神好。但要警惕喷射状呕吐、呕吐物颜色异常、或伴随精神差等情况。',
      commonMisconception: '宝宝一吐奶就认为是生病了，立刻带去医院。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '瓶喂的正确姿势与技巧',
      oneLineSummary: '宝宝半竖立45°，奶瓶倾斜使奶嘴充满奶液，每次 15-20 分钟匀速喂完，喂后拍嗝。',
      detailedExplanation:
        '正确瓶喂姿势：宝宝保持半竖立约 45°（不要平躺），奶瓶倾斜使奶嘴中始终充满奶液而非空气，这能减少吞入空气引发的胀气和呛奶风险。奶嘴应选择适合月龄的低流速（新生儿用 S 号/0-3月）。喂奶时注意"间歇式喂养"——让宝宝每吸几口换一口气，不要一直灌。每次喂奶约 15-20 分钟匀速完成，喂后可竖直抱 10-15 分钟或轻拍背帮助排气。如果宝宝吃着吃着睡着了，可轻拍脚心或转动奶瓶唤醒，确保吃饱后再入睡。',
      commonMisconception: '让宝宝平躺着喝，拿起奶瓶一直灌到完，越快越好。',
      sourceReference: 'AAP 第7版 第3章/第4章',
      reviewStatus: 'draft',
    },
  ],
  'newborn-feeding-3': [
    {
      order: 1,
      title: '看尿量比看时间更靠谱',
      oneLineSummary: '每天 6-8 片湿尿布是判断奶量是否充足的重要客观指标。',
      detailedExplanation:
        '新生儿出生后前几天尿量少属正常，但一周后通常每天应有 6-8 片湿透的尿布。小便颜色应为淡黄色或无色的。尿量不足可能提示奶量摄入不足。',
      commonMisconception: '只要按时间喂就一定能保证吃饱，不关注排泄指标。',
      sourceReference: 'AAP 第7版 第3章/第4章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '体重增长是最客观的信号',
      oneLineSummary: '新生儿生理性体重下降后，通常在 7-10 天内恢复出生体重，之后按预期增长。',
      detailedExplanation:
        '出生后前几天体重下降 7%-10% 是正常的生理现象（水分丢失）。之后前 3 个月每天约增长 14-28 克。定期测量体重是评估喂养效果的重要方法，但具体数值应由医生评估。',
      commonMisconception: '看到体重下降就立刻加配方奶，不观察整体趋势。',
      sourceReference: 'AAP 第7版 第4章',
      reviewStatus: 'draft',
    },
  ],
  'newborn-sleep-1': [
    {
      order: 1,
      title: '仰卧是最安全的睡姿',
      oneLineSummary: '为降低婴儿猝死综合征（SIDS）风险，新生儿应始终仰卧入睡。',
      detailedExplanation:
        '婴儿猝死综合征（SIDS）是指 1 岁以下婴儿在睡眠中突然死亡、且经过全面检查仍无法解释原因的悲剧。其确切原因尚不完全清楚，但大量研究表明，让婴儿仰卧入睡可将 SIDS 风险降低 50% 以上，这是目前最重要的预防措施。婴儿床上不应放置枕头、毛绒玩具、厚被子等松软物品，以免遮挡口鼻造成窒息。当宝宝学会自己翻身后，可以让他自己选择舒适的睡姿，但仍应从仰卧开始。',
      commonMisconception: '担心宝宝头型变扁，让新生儿侧睡或趴睡。',
      sourceReference: 'AAP 第7版 第3章/第15章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '创建安全的睡眠环境',
      oneLineSummary: '用睡袋替代被子，坚硬的床垫、紧绷的床单，同室不同床，是安全睡眠的核心要素。',
      detailedExplanation:
        'AAP 建议婴儿与父母同室但不同床至少到 6 个月（理想情况到 1 年）。使用可穿式毯子/睡袋代替被子，避免覆盖面部。婴儿床不要放在窗户旁、绳索旁等有潜在危险的区域。',
      commonMisconception: '冬天怕宝宝冷，给宝宝盖厚被子、加枕头。',
      sourceReference: 'AAP 第7版 第3章/第15章',
      reviewStatus: 'draft',
    },
  ],
  'newborn-sleep-2': [
    {
      order: 1,
      title: '频繁夜醒是正常发育现象',
      oneLineSummary: '新生儿睡眠周期约 50-60 分钟，浅睡眠比例高，频繁醒来不是"坏习惯"。',
      detailedExplanation:
        '新生儿的睡眠模式与成人不同，他们需要频繁醒来进食、寻求安全感。这个过程对大脑发育有帮助。通常到 3-4 个月后，昼夜节律逐渐成熟，睡眠会慢慢变长。',
      commonMisconception: '认为新生儿应该睡整觉，频繁醒来是"惯坏了"。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '夜醒不只是饿了',
      oneLineSummary: '夜醒原因可能是尿布湿了、太冷或太热、需要安抚，不一定每次都要喂奶。',
      detailedExplanation:
        '宝宝夜间醒来时，先观察状态，再按逻辑排查：尿布是否需要更换、温度是否合适、是否有不适。如果都不是，可能只是需要安抚。夜间喂奶和护理时应保持安静、光线暗，避免过度刺激。',
      commonMisconception: '一听到宝宝夜里哭就马上喂奶，不排查其他原因。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
  ],
  'newborn-sleep-3': [
    {
      order: 1,
      title: '帮宝宝区分白天和晚上',
      oneLineSummary: '白天有光、有互动，晚上安静、昏暗，帮宝宝逐步建立昼夜节律。',
      detailedExplanation:
        '新生儿的昼夜节律尚未成熟。照护者可以在白天保持正常光线、与宝宝互动玩耍、做俯趴练习（Tummy Time）；晚上则调暗灯光、保持安静、减少刺激。这有助于宝宝逐渐学会白天醒、晚上睡。',
      commonMisconception: '以为宝宝天生就能区分昼夜，不需要特别引导。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '建立睡前程序很重要',
      oneLineSummary: '固定的睡前流程（洗澡→喂奶→抚触→昏暗环境）能帮宝宝建立入睡预期。',
      detailedExplanation:
        '每天在相似的时间进行相似的睡前活动（如温水洗澡、轻柔抚触、唱摇篮曲），宝宝会逐渐将这些活动与睡眠关联起来。睡前程序不必复杂，关键是保持一致性。',
      commonMisconception: '觉得睡前程序太麻烦，没有坚持做。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 日常护理模块 - 关卡 1：脐带护理
  // ==========================================
  'newborn-care-1': [
    {
      order: 1,
      title: '脐带残端保持清洁干燥即可',
      oneLineSummary: '现代指南不推荐用酒精擦拭脐带残端，保持清洁干燥就是最好的护理。',
      detailedExplanation:
        '脐带残端通常在出生后 1-3 周内自然萎缩脱落。期间只需保持脐带部位清洁干爽，无需使用酒精消毒。洗澡后应将脐带处轻轻擦干。给宝宝穿纸尿裤时，将尿布上缘折到肚脐以下，避免尿液浸湿脐带残端。',
      commonMisconception: '以为必须每天用酒精棉签擦拭脐带根部才能防止感染。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '脐带脱落时少量出血是正常的',
      oneLineSummary: '脐带脱落的几天里可能在尿布上发现几滴血迹，属于正常现象。',
      detailedExplanation:
        '脐带自然脱落过程中，脐根部会形成小创面，排出少量血性或淡黄色分泌物是正常的。只要出血量少、宝宝精神好、脐周皮肤无红肿，就不必担心。但如果出血持续不止，或脐带根部周围的皮肤发红、肿胀、有黄色脓性分泌物，就需要及时就医。',
      commonMisconception: '看到脐带处有一点点血迹就以为感染了，非常紧张。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '警惕脐带感染的信号',
      oneLineSummary: '脐带处出现黄色有异味的分泌物、周围皮肤发红、触碰时宝宝哭闹，都是感染信号。',
      detailedExplanation:
        '脐带感染虽然较少见，但需要认真对待。感染信号包括：脐周皮肤明显发红、肿胀、有黄色或绿色带臭味的分泌物、触碰脐带区域时宝宝抗拒或哭闹、宝宝可能伴有发热。还有一种情况是脐肉芽肿——脐带脱落后留下一小块红色湿润的"肉疙瘩"，可能持续渗出淡黄色液体，一般需由医生处理。如果3周后脐带仍未脱落，也应咨询医生。',
      commonMisconception: '以为脐带残端发黑发臭是正常现象，一直不去处理。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 日常护理模块 - 关卡 2：洗澡
  // ==========================================
  'newborn-care-2': [
    {
      order: 1,
      title: '脐带未脱落前只做擦浴',
      oneLineSummary: '脐带残端完全愈合前，应用擦浴代替盆浴，避免脐带浸泡在水中。',
      detailedExplanation:
        '脐带未脱落的几周内，应采用擦浴方式：在温暖的房间里，将宝宝放在铺了柔软浴巾的平面上，用温水和低刺激性沐浴液，按"脸→身体→尿布区域"的顺序依次擦洗，始终用浴巾包裹未清洗的部位以防着凉。脐带脱落后、肚脐完全愈合（不再有渗液），才可以尝试盆浴。',
      commonMisconception: '以为新生儿出生后就应该放进浴盆里洗。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '洗澡水温与安全是第一位',
      oneLineSummary: '水温应温热不烫（手腕内侧感觉舒适），水深不超过 5cm，永远不要将宝宝独自留在水中。',
      detailedExplanation:
        '最合适的水温是用手腕或手肘内侧测试感到温热即可（约37-38℃），水深约5厘米。从水龙头接水时，一定先放冷水再放热水（关闭时先关热水再关冷水），避免烫伤。家用热水器最高温度建议设在49℃以下。最关键的安全原则：无论任何情况，绝对不要把宝宝独自留在浴盆中，哪怕只有一秒钟——需要离开时必须抱着宝宝一起走。',
      commonMisconception: '觉得"去拿个毛巾很快"，把宝宝独自留在浴盆里几秒钟。',
      sourceReference: 'AAP 第7版 第3章 和 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '洗澡频率和护肤',
      oneLineSummary: '1 岁以下的婴儿每周用沐浴液洗 3 次即可，过于频繁洗澡反而会使皮肤干燥。',
      detailedExplanation:
        '只要每次换尿布时彻底清洁了尿布区域，婴儿不需要每天用沐浴液洗澡。频繁洗澡和过度使用肥皂会破坏婴儿皮肤的保护层。洗完后用毛巾轻轻拍干（不要用力擦），立即涂上不含香料的低致敏润肤乳来锁住水分。新生儿衣物应至少漂洗两次，以去除残留的化学物质和洗涤剂，减少对敏感皮肤的刺激。',
      commonMisconception: '觉得要每天用沐浴液给宝宝洗得"干干净净"，洗完也不涂润肤乳。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 日常护理模块 - 关卡 3：换尿布
  // ==========================================
  'newborn-care-3': [
    {
      order: 1,
      title: '大便后应立即更换尿布',
      oneLineSummary: '大便中的消化酶会刺激皮肤，长时间接触是尿布疹最主要的原因。',
      detailedExplanation:
        '大便中含有帮助消化的酶类物质，会侵蚀婴儿娇嫩的皮肤。每次大便后应立即更换尿布，用温水和柔软的布（或婴儿湿巾）彻底清洁臀部、生殖器和大腿根部，然后轻轻拍干。可以的话，换上新尿布前让宝宝的臀部在空气中晾几分钟，有助于保持干爽。',
      commonMisconception: '觉得尿布没太湿就不用换，等"装满了"再换。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '尿布疹的预防与应对',
      oneLineSummary: '勤换尿布、保持干爽、适当通风，是预防尿布疹的三大原则。',
      detailedExplanation:
        '尿布疹的初期表现为臀部、下腹部、生殖器区域的皮肤发红或出现细小疹子。预防的核心是减少皮肤与尿液、大便的接触时间。如果已经出现尿布疹，每次换尿布后涂抹含氧化锌的护臀膏形成保护屏障，通常 2-3 天内会明显改善。如果皮疹持续加重或出现脓疱、破溃，需咨询儿科医生——可能合并了酵母菌感染。',
      commonMisconception: '尿布疹一出现就涂爽身粉或用偏方，不去找根本原因。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '观察排泄物是了解宝宝健康的重要窗口',
      oneLineSummary: '每天 6-8 片湿尿布是水分充足的标志，尿液颜色过深提示可能摄入不足。',
      detailedExplanation:
        '健康新生儿每天尿湿 4-6 片以上尿布（通常1-3小时排尿1次）。正常的尿液呈淡黄色至深黄色；颜色越深表明尿液越浓缩，可能提示摄入水分不足。出生后第1周尿布上可能出现粉色或红褐色痕迹，通常是尿液浓缩所致而非出血，只要每天尿布湿的次数足够就不必过于紧张。如果尿液中有真正血迹、或伴随发热、食欲不振等其他症状，应及时就医。',
      commonMisconception: '看到尿布有粉色痕迹就认为宝宝尿血了。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 安全防护模块 - 关卡 1：防呛奶
  // ==========================================
  'newborn-safety-1': [
    {
      order: 1,
      title: '呛奶的常见原因与预防',
      oneLineSummary: '喂奶姿势不当或奶流速过快是呛奶的主要原因，调整姿势和流速可以有效预防。',
      detailedExplanation:
        '新生儿呛奶通常发生在喂奶过程中：喂奶姿势让宝宝吞咽不及、奶瓶奶嘴孔过大导致流速过快、或宝宝过于饥饿导致吞咽急促。瓶喂时应选择适合新生儿的小孔奶嘴（S 号/低速），喂奶时将奶瓶倾斜，确保奶嘴中充满奶液而非空气，并控制倾斜角度来调节流速。喂奶过程中让宝宝保持半竖立的姿势，不要完全平躺。若发现流速过快宝宝来不及吞咽，立即放低奶瓶角度或暂停几秒。',
      commonMisconception: '呛奶只是"喝太急"，不用特别调整喂养方式。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '呛奶发生时的正确处理（分两级）',
      oneLineSummary: '轻度呛咳让宝宝自己咳出；重度窒息（无法出声/呼吸）需面部朝下、头低于躯干，5次背击+5次胸压急救。',
      detailedExplanation:
        '呛奶有两级，处理方式完全不同。①轻度呛咳（宝宝能咳嗽、能哭出声、能呼吸）：立即停止喂奶，让宝宝保持前倾略低头的姿势，依靠宝宝自身的咳嗽反射将奶液咳出——不要用力拍背，强拍可能将奶液震入气道更深处。②重度窒息（宝宝无法咳嗽/哭/呼吸，面色发青）：立即采用婴儿窒息急救法——将宝宝面部朝下放在前臂上，用手托住头部和下颌，保持头部低于躯干，用掌根在两肩胛骨之间用力拍击5次；若未排出，翻转宝宝面部朝上（仍保持头低于躯干），用两根手指在胸骨下半段快速按压5次（深度约4厘米）；重复交替背击+胸压，同时呼叫急救电话。禁止将手指伸入宝宝口中抠取——可能将奶液推得更深。',
      commonMisconception: '呛奶时把宝宝竖直抱起来拍背——这是危险的做法，可能使奶液更深地落入气道。',
      sourceReference: 'AAP 第7版 第23章（呼吸道异物阻塞）；AAP HealthyChildren.org',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '喂奶后的正确姿势',
      oneLineSummary: '喂完奶后不要立即将宝宝平放，应竖抱约 20-30 分钟，帮助减少溢奶和呛奶风险。',
      detailedExplanation:
        '喂完奶后应竖抱或让宝宝保持半坐姿势 20-30 分钟，利用重力帮助奶液留在胃中。这段时间内避免剧烈晃动、换尿布或让宝宝俯趴。如果宝宝睡着了，可以竖抱着睡一会儿再慢慢放平。拍嗝可以在喂奶过程中进行，不必非等到吃完。如果宝宝经常吐奶且伴有烦躁、拒绝进食、体重增长不良，应咨询医生排除胃食管反流等问题。',
      commonMisconception: '喂完奶就把宝宝平放，认为只要拍了嗝就安全了。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 安全防护模块 - 关卡 2：防跌落
  // ==========================================
  'newborn-safety-2': [
    {
      order: 1,
      title: '永远不要将宝宝独自留在高处',
      oneLineSummary: '即使新生儿还不能翻身，也可能在瞬间扭动身体而发生跌落。',
      detailedExplanation:
        '新生儿虽然还不能主动翻身，但他们会有不自主的肢体抽动和扭动。绝对不要将宝宝独自放在尿布更换台、床、沙发、桌子等任何高于地面的台面上，哪怕只是"转身拿个东西"。如果需要离开，哪怕只有几秒，也应将宝宝抱起或放在安全的低处（如地板上的游戏垫）。换尿布时，务必始终用一只手扶着宝宝。',
      commonMisconception: '觉得"宝宝还小不会动"，暂时离开几秒没关系。',
      sourceReference: 'AAP 第7版 第15章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '婴儿床和婴儿用品的安全标准',
      oneLineSummary: '使用符合安全标准的婴儿床、正确安装护栏、不放松软物品，是预防跌落的关键。',
      detailedExplanation:
        '选择符合现行安全标准的婴儿床，护栏栏杆间距应小于 6 厘米以防宝宝头部卡住。床垫必须与婴儿床内部尺寸完全匹配，放入后任何一边不留超过两指宽的缝隙。当宝宝能坐起时就要降低床板高度，能站立时应将床板调至最低。婴儿床不要放在窗户旁边，窗帘拉绳等带状物应远不可及。不要在婴儿床上方悬挂画框或搁架。',
      commonMisconception: '用二手婴儿床省事省钱，不检查是否符合安全标准。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '抱着宝宝时注意安全',
      oneLineSummary: '抱着宝宝上下楼、行走时确保视野不被遮挡，扶手和防滑措施必不可少。',
      detailedExplanation:
        '抱着新生儿走路时，要特别注意脚下——地毯边缘、散落的玩具、湿滑的地面都可能造成绊倒。上下楼梯时一定要扶着扶手，宝宝稍微大一些后不要一手抱宝宝一手拿东西走楼梯。给尿布更换台选择位置时，应放在地毯或软垫上，并紧靠墙壁但不能紧靠窗户，避免宝宝从窗户跌落。将常用物品放在更换台旁伸手可及的位置，这样你就不需要离开。',
      commonMisconception: '一手抱娃一手拿手机/物品上下楼梯觉得没问题。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 安全防护模块 - 关卡 3：安全抱姿
  // ==========================================
  'newborn-safety-3': [
    {
      order: 1,
      title: '始终支撑头部和颈部',
      oneLineSummary: '新生儿颈部肌肉无力，抱起和放下时手必须始终托住头部和颈部。',
      detailedExplanation:
        '新生儿出生后头几个月，颈部肌肉尚未发育到足以支撑头部的程度。抱起宝宝时，一只手托住头颈，另一只手托住臀部；将他从怀里放下时，也要先让身体着床，再轻轻放开托头的手。绝对不要像抱大孩子那样只从腋下抱起来。如果使用婴儿背带或包裹巾，也必须确保宝宝的头部得到良好支撑，下巴不要贴近胸口，保持呼吸道通畅。',
      commonMisconception: '觉得宝宝脖子已经有点力气了，偶尔不托头也没关系。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '不要剧烈摇晃宝宝',
      oneLineSummary: '摇晃婴儿综合征可造成严重的脑损伤，任何情况下都不要用力摇晃婴儿。',
      detailedExplanation:
        '摇晃婴儿综合征是用力摇晃婴儿导致的严重脑损伤，可造成永久性的残障甚至死亡。婴儿头部相对较大、颈部肌肉很弱、脑组织柔软，剧烈摇晃会导致脑血管撕裂、脑内出血。哪怕只是"玩闹式"的抛接、转圈，对新生儿来说也非常危险。如果宝宝哭闹让你情绪激动，先将宝宝安全地放在婴儿床中，自己离开房间冷静几分钟，再回去安抚。',
      commonMisconception: '觉得"我就轻轻晃一下"，或把宝宝抛起来玩是亲密互动。',
      sourceReference: 'AAP 第7版 第15章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '安全使用婴儿背带和包裹巾',
      oneLineSummary: '使用背带时要确保能看到宝宝的脸、呼吸道不受阻挡，遵循 T.I.C.K.S 原则。',
      detailedExplanation:
        '使用婴儿背带或包裹巾时，可以参考 T.I.C.K.S 安全原则：Tight（紧贴）、In view at all times（始终能看到宝宝）、Close enough to kiss（近到可以亲吻）、Keep chin off chest（下巴不贴近胸口）、Supported back（背部有良好支撑）。包裹巾不能绑得太松——太松宝宝可能滑落；也不能把宝宝裹得头都看不见或脸部完全贴在布上。每次使用前检查扣子和缝线是否完好。',
      commonMisconception: '把宝宝裹得严严实实看不见脸觉得"这样暖和"，没意识到窒息风险。',
      sourceReference: 'AAP 第7版 第3章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 发育观察模块 - 关卡 1：黄疸观察
  // ==========================================
  'newborn-dev-1': [
    {
      order: 1,
      title: '什么是新生儿黄疸',
      oneLineSummary: '黄疸是新生儿期最常见的问题之一，皮肤和眼白发黄是因为血液中胆红素升高。',
      detailedExplanation:
        '黄疸是由血液中胆红素增多导致皮肤和眼白（巩膜）变黄的现象。新生儿黄疸很常见，因为：新生儿的红细胞寿命较短、肝脏处理胆红素的功能尚未成熟、肠肝循环中胆红素重吸收较多。大多数新生儿黄疸是生理性的，在出生后 2-4 天出现，约 1-2 周内自然消退。但有些黄疸可能是病理性的，需要医学干预。部分宝宝的黄疸消退可能稍慢（迁延性黄疸），只要宝宝精神好、喂养正常、体重增长正常，通常也是良性过程。',
      commonMisconception: '看到宝宝发黄第一反应是"肝不好"，非常恐慌。',
      sourceReference: 'AAP 第7版 第3章/第5章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '如何观察黄疸程度',
      oneLineSummary: '在自然光下按压宝宝皮肤观察底色，黄疸通常从头到脚发展，消退则是从脚到头。',
      detailedExplanation:
        '观察黄疸时，应在自然光（非灯光）下用手指轻压宝宝的皮肤（如额头、鼻子或胸骨），观察按压后皮肤底色的黄染程度。黄疸通常遵循"从头到脚"出现、"从脚到头"消退的规律。如果黄疸仅局限于面部和上半身，通常程度较轻；如果腹部、大腿、小腿甚至手心脚心也明显发黄，说明胆红素水平可能较高，需要就医检测。此外，眼白变黄也是重要的观察指标。',
      commonMisconception: '只在昏暗的灯光下看一眼就说"不黄"，忽视准确观察方法。',
      sourceReference: 'AAP 第7版 第5章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '黄疸的危险信号',
      oneLineSummary: '黄疸出现过早、程度重、持续不退，或伴有精神差、拒奶、大便颜色异常，需立即就医。',
      detailedExplanation:
        '需要警惕的危险信号包括：出生 24 小时内就出现黄疸；黄疸程度迅速加重（身体下半部分明显发黄）；宝宝精神萎靡、嗜睡、吸吮无力、哭声尖直；大便颜色变浅呈陶土色（可能提示胆道问题）；黄疸超过 2 周仍不退或反而加重。对早产儿或出生体重较低的宝宝，黄疸的观察更应严密。治疗严重黄疸最常用且有效的方法是光疗（让宝宝在特殊蓝光下照射）。',
      commonMisconception: '认为"黄疸晒晒太阳就好了"，没去判断严重程度。',
      sourceReference: 'AAP 第7版 第5章；WHO 新生儿指南',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 发育观察模块 - 关卡 2：体重变化
  // ==========================================
  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡1：喂养节奏的变化
  // ==========================================
  'stage1-feeding-1': [
    {
      order: 1,
      title: '满月后胃容量大了，单次吃得更多',
      oneLineSummary: '满月后宝宝胃容量显著增大，单次吃奶量增加，喂奶间隔自然从2-3小时拉长到3-4小时。',
      detailedExplanation:
        '随着宝宝成长，胃从出生时的弹珠大小逐步增大。满月后胃容量增大，单次能吃到的奶量随之增加。这意味着宝宝不需要像新生儿期那样每2-3小时就必须吃一次。配方奶消化慢约3-4小时，满月后喂奶间隔自然拉长到约3-4小时一次。但不要用固定时间表来约束宝宝——观察饥饿信号比看钟表更科学。',
      commonMisconception: '觉得2个月大的宝宝必须严格按照3小时一次喂，少一分钟都不行。',
      sourceReference: 'AAP 第7版 第7章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '夜间喂奶次数逐步减少是正常的',
      oneLineSummary: '宝宝2个月时夜间喂奶减至1-2次，将更多睡眠集中在夜晚，这是神经系统成熟的标志。',
      detailedExplanation:
        '新生儿期夜间需要频繁吃奶，但满月后随着胃容量增大和神经系统成熟，宝宝夜间的连续睡眠时间会逐渐拉长。2个月时，许多宝宝夜间可连续睡5-6小时，夜间只需喂1-2次。到3个月时可能延长到6-8小时。但每个宝宝的节奏不同，有的可能仍需要夜间喂2次。只要宝宝白天精神好、体重正常增长，夜间喂奶次数减少就是正常的发育进程。',
      commonMisconception: '觉得宝宝2个月了夜里还要吃好几次奶就是"有问题"，试图强行断夜奶。',
      sourceReference: '梅奥育儿全书 第十八章/第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '猛长期来了不要慌',
      oneLineSummary: '宝宝在第3-6周会迎来猛长期，表现得频繁要吃、烦躁不安，坚持按需喂养2-3天就会自行恢复。',
      detailedExplanation:
        '猛长期（growth spurt）是宝宝体重快速增长的自然阶段，通常在出生后第7-10天和第3-6周各出现一次。在猛长期，宝宝会表现得异常"贪吃"——几乎每1-2小时就想吃一次奶，可能伴随烦躁和睡眠不稳。配方奶喂养的宝宝在猛长期可能需要每次多加 10-20ml，坚持按需喂养 2-3 天后通常会恢复稳定。但不应无限制加量——观察宝宝的饱腹信号（松开奶嘴、不再吞咽、转头），吃饱后及时停止，避免过度喂养。',
      commonMisconception: '猛长期宝宝频繁要吃时就一直加量，多多益善。',
      sourceReference: '梅奥育儿全书 第十七章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡2：喂养误区与边界
  // ==========================================
  'stage1-feeding-2': [
    {
      order: 1,
      title: '不要给1-3个月的宝宝喂任何"辅食"',
      oneLineSummary: '6个月之前宝宝只需要配方奶，果汁、米汤、固体食物都不能喂。',
      detailedExplanation:
        '在1-3个月阶段，宝宝的消化系统、口腔运动能力和免疫功能都还没有准备好接受除配方奶以外的任何食物。吐舌反射在4个月之前非常强大——宝宝会自动用舌头将放入口中的任何固体推出来，这是保护性反射。过早添加固体食物或果汁不仅不能帮助宝宝长得更好，反而可能取代配方奶中的重要营养、增加过敏风险和呛咳窒息风险。即使到6个月以后添加果汁，也应用杯子而非奶瓶，每天不超过120毫升。',
      commonMisconception: '老人说"喝点米汤长得壮""给点果汁补充维生素"，新手父母容易被说服。',
      sourceReference: 'AAP 第7版 第7章；梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '不要用喂奶作为唯一的安抚方式',
      oneLineSummary: '宝宝哭泣不一定是饿——排查尿布、温度、胀气、安抚需求后再判断是否需要再次喂奶。',
      detailedExplanation:
        '1-3个月的宝宝哭泣原因很多：尿布湿了、太热或太冷、需要安抚、胀气不适、过度刺激等。如果在刚刚喂完奶后宝宝又哭了，应先尝试其他安抚方式（如抱起来轻声说话、检查尿布和体温、轻轻按摩腹部），而不是立即再次喂奶。频繁用喂奶作为唯一回应会让宝宝将"吃奶"和"求安抚"混淆，还容易导致过度喂养和胀气不适。但这不代表不要按需喂养——如果宝宝确实表现出明确的饥饿信号（转头、张嘴、吸吮动作），当然要及时喂养。',
      commonMisconception: '宝宝一哭就塞奶瓶喂奶，不排查其他原因。',
      sourceReference: '梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '不要仅凭外表判断宝宝"太胖"或"太瘦"',
      oneLineSummary: '婴儿肥不等于肥胖，前6个月是宝宝脂肪高速积累的正常阶段，不要给婴儿节食。',
      detailedExplanation:
        '看到1-3个月宝宝"肉嘟嘟"的脸颊，一些家长会担心"是不是过度喂养了"。实际上，婴儿期需要高脂肪含量的饮食来支持大脑和神经系统发育——神经纤维周围的绝缘层（髓鞘）由大量脂肪构成。绝大多数"婴儿肥"会在宝宝开始爬行和走路后自然消失。关键是看生长趋势——体重沿自己的生长曲线稳定增长就不用担心。但也不要过度喂养：遵循宝宝的饥饿和饱腹信号（吃饱了会自己停下来或推开奶瓶），这能帮助宝宝建立自我调节能力。',
      commonMisconception: '觉得宝宝太胖了偷偷少喂一点，或者一哭就多喂很多。',
      sourceReference: '梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 喂养模块 - 关卡3：吐奶升级与胃食管反流
  // ==========================================
  'stage1-feeding-3': [
    {
      order: 1,
      title: '生理性溢奶3个月内仍常见',
      oneLineSummary: '少量吐奶在3个月以内仍然非常常见，是因为贲门（胃与食道连接处）肌肉尚未发育成熟。',
      detailedExplanation:
        '贲门是一圈环形肌肉，负责在胃入口处"关门"防止返流。婴儿的贲门肌肉在出生时还很松弛，通常需到4-6个月才能发育强壮。因此1-3个月的宝宝吃完奶后少量溢奶很正常——奶液从嘴角流出几口，宝宝精神好、不哭闹、体重正常增长。这不同于病理性的"吐奶"——吐奶是胃内容物被强力排出，通常量更大。大多数宝宝在4-6个月后随着贲门发育和竖坐时间增多，溢奶会自然减少。',
      commonMisconception: '宝宝过了满月还吐奶，觉得"怎么这么大了还吐"，认为不正常。',
      sourceReference: 'AAP 第7版 第7章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '区分胃食管反流（GERD）与正常溢奶',
      oneLineSummary: 'GERD不只是"吐得更多"——关键信号是宝宝在喂养时弓背哭闹、拒食、体重增长不良。',
      detailedExplanation:
        '当生理性溢奶升级为胃食管反流病（GERD）时，胃酸返流到食道会造成疼痛。需要警惕的信号：①每次喂养后都大量呕吐（喷射状或连续多次）；②喂养过程中宝宝弓背、扭动、哭闹——因为胃酸反流灼伤食道带来疼痛；③宝宝开始抗拒吃奶（因为吃奶=疼痛的条件反射）；④体重增长不良——吐出的奶量太多，实际摄入不足。如果出现以上任何组合，应咨询儿科医生，不要自行换奶粉或用药。大多数GERD在1岁以内会自然好转。',
      commonMisconception: '看到宝宝吐奶多就自行换奶粉或给宝宝吃消化药，不去排查GERD。',
      sourceReference: 'AAP 第7版 第7章/第16章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '减少吐奶的实用技巧',
      oneLineSummary: '少量多次喂养、喂奶中拍嗝1-2次、喂后竖抱20-30分钟，能有效减少吐奶。',
      detailedExplanation:
        '几个经过验证有效减少吐奶的方法：①少量多次喂养——每次喂少一点、喂频繁一点，避免胃被撑得太大；②喂奶过程中途拍嗝1-2次，及时排出吞入的空气；③喂完奶后竖抱或保持半坐姿势20-30分钟，利用重力让奶留在胃中；④避免在喂奶后立即换尿布、俯趴或剧烈摇晃宝宝。如果宝宝容易吐奶，可以在婴儿床床垫下垫一个小角度，让床头略高于床尾。喂奶后竖抱时注意不要挤压宝宝的腹部。',
      commonMisconception: '喂完奶马上把宝宝平放睡觉，认为"吐了就吐了没什么大不了"。',
      sourceReference: 'AAP 第7版 第7章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡1：睡眠规律的形成
  // ==========================================
  'stage1-sleep-1': [
    {
      order: 1,
      title: '1-3个月：昼夜节律开始建立',
      oneLineSummary: '2个月左右宝宝开始能区分白天和黑夜，夜间睡眠时间逐渐延长至5-8小时。',
      detailedExplanation:
        '新生儿没有昼夜概念，但到6-8周时，宝宝的松果体开始分泌褪黑素，逐步建立起昼夜节律。你会注意到宝宝夜间连续睡眠的时间变长了，白天醒着的时间也更多。帮助促进这个进程的方法：白天保持房间明亮、有正常活动声音；夜间喂奶时保持灯光昏暗、不说话、不互动，喂完就放回去。这个阶段的"睡整觉"通常指连续5-8小时，而不是成人的8-10小时。大部分宝宝到3个月时能连续睡6-8小时，但个体差异很大。',
      commonMisconception: '期望2个月宝宝就能像成人一样睡整夜，做不到就觉得有问题。',
      sourceReference: 'AAP 第7版 第7章/第15章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '小睡规律开始成型但还不稳定',
      oneLineSummary: '1-3个月宝宝白天通常需要3-4次小睡，每次30分钟到2小时不等，规律尚未定型。',
      detailedExplanation:
        '与新生儿几乎全天"吃了睡睡了吃"不同，1-3个月的宝宝白天的清醒时间明显增多。典型模式：醒1-2小时后需要小睡，全天共3-4次小睡。但此时小睡的时长和节奏还不稳定——有时只睡30分钟就醒，有时一睡就是2小时。这阶段的重点是观察宝宝的疲倦信号（揉眼睛、打哈欠、烦躁、对互动失去兴趣），而不是死板地卡时间。错过"睡眠窗口"会导致宝宝过度疲劳反而更难入睡。',
      commonMisconception: '要求2个月宝宝"上午9点准时小睡"，把婴儿当机器。',
      sourceReference: '梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '识别婴儿的疲倦信号',
      oneLineSummary: '打哈欠、揉眼睛、眼神呆滞或烦躁不安是宝宝需要入睡的信号，抓住时机事半功倍。',
      detailedExplanation:
        '和新生儿不同，1-3个月的宝宝在疲倦时会有更明显的信号：反复打哈欠、揉眼睛和耳朵、眼神发直呆滞、对玩具和互动失去兴趣、无故烦躁哭闹。看到早期信号时及时让宝宝入睡，通常比等到大哭后再哄容易得多。如果宝宝已经过度疲劳并大哭，可以先安抚平静再尝试入睡，而不是在哭闹中强硬放下。和学习任何技能一样，入睡能力随着月龄增长逐渐发展，这个阶段仍然需要成人帮助。',
      commonMisconception: '认为"让他哭一会儿就会自己睡"，对2个月宝宝采取"哭声免疫法"。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡2：SIDS预防升级
  // ==========================================
  'stage1-sleep-2': [
    {
      order: 1,
      title: 'SIDS高峰期在2-4个月',
      oneLineSummary: '2-4个月是SIDS最高发期，仰卧、硬床垫、无松软物品、同室不同床是最核心的4项防护。',
      detailedExplanation:
        '很多家长以为过了新生儿期SIDS风险就降低了，但实际上SIDS的高峰期恰好在2-4个月。这个阶段仍然必须严格执行安全睡眠规则：①始终仰卧（即使宝宝已经能抬头）；②使用硬质婴儿床垫，铺紧床单；③床上没有任何枕头、毯子、毛绒玩具、床围；④宝宝睡在父母卧室的独立小床上（同室不同床）至少到6个月，最好到1岁。AAP建议不要与婴儿同床睡，尤其是在沙发或椅子上睡着时抱着宝宝极度危险。',
      commonMisconception: '觉得"宝宝都2个月了，不会那么脆弱了吧"，开始在床上放枕头或玩具。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '襁褓的使用与停止时机',
      oneLineSummary: '襁褓帮助安抚小月龄宝宝，但一旦宝宝出现翻身迹象（约2-3个月）就必须立即停止。',
      detailedExplanation:
        '襁褓能模拟子宫内的包裹感，减少惊跳反射的干扰，帮助宝宝睡得更安稳。但襁褓必须松紧适度——髋部要有足够空间让腿自由活动，过紧的襁褓可能增加髋关节发育不良的风险。更重要的是：一旦宝宝出现任何翻身迹象（侧身、从仰卧翻到侧卧），必须立即停止使用襁褓。裹着襁褓的宝宝一旦翻身成俯卧，无法自己翻回来，有极高的窒息风险。多数宝宝在2-3个月时开始出现翻身迹象，这个时间点必须停止襁褓。',
      commonMisconception: '到3个多月了还裹着襁褓，觉得"裹着睡得香"。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '安抚奶嘴可降低SIDS风险',
      oneLineSummary: '入睡时使用安抚奶嘴已被证明能降低SIDS风险，可在喂养规律稳定后引入（约3-4周）。',
      detailedExplanation:
        '多项研究证明，在宝宝入睡时使用安抚奶嘴可以显著降低SIDS的发生率，原因可能与保持气道通畅和减少深度睡眠有关。AAP建议在宝宝入睡时给安抚奶嘴——如果睡着了奶嘴掉了，不需要重新塞回去。建议等宝宝喂养规律基本稳定（约3-4周）后再引入安抚奶嘴，避免干扰正常喂养信号。不要用绳子把奶嘴系在宝宝脖子或衣服上，有勒颈风险；也不要强迫宝宝接受奶嘴。',
      commonMisconception: '听说安抚奶嘴不好，完全拒绝使用，错过降低SIDS风险的益处。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 睡眠模块 - 关卡3：睡眠倒退与应对
  // ==========================================
  'stage1-sleep-3': [
    {
      order: 1,
      title: '3个月前后的"睡眠倒退"是正常发育现象',
      oneLineSummary: '约3个月时宝宝的睡眠模式从婴儿型向成人型转化，夜间醒来更频繁是大脑成熟的标志。',
      detailedExplanation:
        '许多家长发现宝宝在3个月左右突然"不会睡了"——以前能睡5-6个小时的宝宝突然频繁夜醒。这其实是好的信号：宝宝的睡眠结构正在从出生时的快速眼动睡眠（REM）主导、转为非快速眼动睡眠（NREM）主导，更接近成人模式。在这个转化期，宝宝在睡眠周期转换时更容易醒来，且可能不会自动接觉。这不是退步，而是神经系统成熟的表现。应对方法：保持一致的睡前程序、夜间醒来时先观察几秒钟再介入、用轻拍而非抱起的方式安抚。大约2-4周后会自然好转。',
      commonMisconception: '发现宝宝睡得更差了，以为是自己做错了什么，自责或开始各种干预。',
      sourceReference: '梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '建立（弹性）睡前程序',
      oneLineSummary: '简单一致的三步睡前程序——洗澡、喂奶、安抚——帮助宝宝知道"该睡了"。',
      detailedExplanation:
        '1-3个月是引入睡前程序的理想时期。有效的睡前程序不需要复杂：温水洗澡→穿上睡衣→在昏暗的光线下最后一次喂奶→轻声唱歌或读书→把宝宝放床上但还醒着（drowsy but awake）。整个过程约20-30分钟。关键是"一致"——同样的顺序、同样的环境。但如果宝宝今天特别闹或者打疫苗后不舒服，当然要以抚慰需求优先。睡前程序的目的是提供可预测的安全感，好的程序是最好的"睡眠暗示"。',
      commonMisconception: '认为睡前程序必须严格到分钟，或者根本不做，宝宝玩累了自然会睡。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '"放下醒"不是坏习惯',
      oneLineSummary: '许多1-3个月宝宝一放床上就醒，这是未发育成熟的前庭系统和安全感需求的正常反应。',
      detailedExplanation:
        '抱在怀里睡得香，一放到床上就醒——这是1-3个月阶段极其普遍的现象。原因：婴儿的睡眠周期比成人短（约50-60分钟），第一个入睡阶段是浅睡眠，大约20分钟后才进入深睡眠；再加上本能的"惊跳反射"和前庭系统对位置变化的敏感，被放下时感觉到了变化就会惊醒。技巧：等宝宝进入深度睡眠（手臂完全放松、呼吸平稳深沉）后再放；放下的动作要慢且稳，屁股先着床、再放头；或尝试让宝宝在半睡半醒时（drowsy but awake）自己躺在小床上入睡。随着神经系统成熟，这个问题会逐渐好转。',
      commonMisconception: '"天天抱睡总不是办法，让他哭几次就好了"，对2个月宝宝实施哭声训练。',
      sourceReference: 'AAP 第7版 第15章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡1：皮肤与身体变化
  // ==========================================
  'stage1-care-1': [
    {
      order: 1,
      title: '婴儿痤疮：不挤不用药',
      oneLineSummary: '约3-4周时宝宝脸上可能出现红疹（婴儿痤疮），由母体激素引起，清水洗自然消退。',
      detailedExplanation:
        '婴儿痤疮通常在出生后3-4周出现，表现为面部（尤其是脸颊和额头）的红色小丘疹，有时有白头。这是母体激素通过胎盘传给宝宝的正常反应，不是感染也不是过敏。处理唯一正确的方式：每天用温水轻轻洗脸，用干净软毛巾蘸干（不要擦），什么药膏、护肤霜都不要涂，更不要挤压。婴儿痤疮通常在几周到2-3个月内自行消退，不留下疤痕。如果到4个月还不消退或持续加重，可以咨询皮肤科医生。',
      commonMisconception: '看到宝宝脸上长东西，赶紧用各种乳液、药膏涂抹，越涂越严重。',
      sourceReference: 'AAP 第7版 第7章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '乳痂（脂溢性皮炎）的护理',
      oneLineSummary: '乳痂是头皮油脂分泌过多形成的黄色鳞屑，无害但可温和清除，不需特殊药物治疗。',
      detailedExplanation:
        '乳痂（cradle cap）表现为头皮或眉毛部位的黄色、油腻、鳞屑状斑块，在1-3个月宝宝中很常见。原因可能是母体激素刺激皮脂腺过度分泌，与卫生无关。护理方法：洗头前15-20分钟在头皮上涂抹少量婴儿油或矿物油软化鳞屑→用软毛刷轻轻按摩→再用温和婴儿洗发水洗掉。每天一次，几天后鳞屑会逐渐脱落。重要的是不要用指甲抠或用梳子硬刮，可能损伤头皮引发感染。绝大多数乳痂在几周到几个月内自愈。',
      commonMisconception: '觉得乳痂"脏"，用指甲抠或用成人去油洗发水猛洗。',
      sourceReference: '梅奥育儿全书 第十八章/第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '湿疹的识别与基础护理',
      oneLineSummary: '1-3个月可能出现特应性皮炎（湿疹），红色干燥斑块，保湿+避免刺激是第一线处理。',
      detailedExplanation:
        '婴儿湿疹通常在出生后1-3个月首次出现，表现为面部和四肢伸侧的红色、干燥、粗糙斑块，宝宝可能因痒而烦躁和摩擦。虽然过敏可能是原因之一，但更多是皮肤屏障功能不成熟造成的。基础护理：用温水（不热）短暂洗澡（5-10分钟）后，在3分钟内涂抹无香料婴儿保湿霜锁住水分。保持室内湿度40%-50%，避免羊毛等粗糙织物直接接触皮肤。如果大面积、反复发作或破溃渗出，应咨询皮肤科或儿科医生，可能需要处方药物。',
      commonMisconception: '宝宝一长湿疹，第一反应就是立即换特殊奶粉或盲目忌口。',
      sourceReference: 'AAP 第7版 第16章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡2：洗澡与日常卫生升级
  // ==========================================
  'stage1-care-2': [
    {
      order: 1,
      title: '洗澡频率：没必要每天洗',
      oneLineSummary: '1-3个月宝宝每周洗2-3次澡足够，天天洗反而容易导致皮肤干燥。',
      detailedExplanation:
        '新生儿脐带脱落后才开始盆浴比较稳妥。满月后的1-3个月阶段，宝宝的活动量仍有限，皮肤产生的油脂和污垢不多，每周洗澡2-3次完全足够。过度频繁洗澡——尤其是使用热水和沐浴露——会洗掉皮肤表面的保护性油脂，导致皮肤干燥甚至加重湿疹。洗澡水温以手肘内侧感觉温暖不烫为准（约37℃），时间控制在5-10分钟。洗后用软毛巾蘸干（不要来回擦），趁皮肤还微湿时涂抹婴儿保湿霜。',
      commonMisconception: '觉得宝宝"不干净"，天天洗澡，甚至每次都用沐浴露。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '脐带/私处等护理要点过渡',
      oneLineSummary: '脐带脱落后保持脐窝干燥；男宝包皮不要强行翻开；女宝从前向后擦拭。',
      detailedExplanation:
        '到1-3个月时脐带通常已脱落，但仍需保持脐窝干燥清洁，洗澡后用棉签蘸干脐窝。对于未环切的男宝宝：不要强行回翻包皮——包皮在婴儿期自然粘连于龟头，强行翻开会造成撕裂和疤痕，包皮自然分离通常在3-5岁时发生。女宝宝：每次换尿布时用温水从前往后（从前向后，不是后往前）轻轻擦拭，避免将肛门区域细菌带到尿道和阴道口。日常清洁只需清水、不需要任何洗剂或消毒液。',
      commonMisconception: '给男宝换尿布时把包皮往后推着洗，觉得"不翻开来洗不干净"。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '剪指甲要选对时机',
      oneLineSummary: '睡着时是最佳剪指甲时间，用婴儿专用指甲剪，剪平不要剪太深以免甲沟炎。',
      detailedExplanation:
        '1-3个月宝宝的指甲长得很快，每周需要剪1-2次，否则易抓伤自己脸部。最佳时机：宝宝熟睡时，手臂肌肉完全放松不会突然抽动。工具选择：婴儿专用小剪刀或指甲锉。剪法：沿指甲自然弧度剪平，不要剪太圆以免指甲向内生长；手指甲剪完后用手指摸一遍确认没有尖角。如果不小心剪出血，用干净纱布按压止血即可，不要涂任何膏药。宝宝的脚趾甲长得慢，检查后需要再剪。',
      commonMisconception: '觉得宝宝醒时也能操作，结果宝宝一动手被剪破，或者干脆一直不剪。',
      sourceReference: '梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 日常护理模块 - 关卡3：外出与日常活动
  // ==========================================
  'stage1-care-3': [
    {
      order: 1,
      title: '户外活动要从短到长',
      oneLineSummary: '满月后即可逐步增加户外时间，从每天10-15分钟开始，避开强日照时段并做好防晒。',
      detailedExplanation:
        '满月后宝宝可以开始日常户外活动，好处包括：自然光促进褪黑素分泌、帮助建立昼夜节律；户外新鲜空气和活动对宝宝和照护者都有积极的心理影响。建议从每天10-15分钟开始，观察宝宝适应后逐步增加。注意事项：6个月以下婴儿不应直接暴露在阳光下——皮肤太嫩、黑色素不足。防晒方法包括遮阳伞、婴儿车遮阳棚、长袖连体衣和宽檐帽。AAP建议6个月以下婴儿不要使用防晒霜（皮肤吸收风险），6个月以上可在小面积涂抹婴儿专用防晒霜。',
      commonMisconception: '给宝宝穿很薄的衣服推出去晒太阳，或者6个月以下就大面积涂防晒霜。',
      sourceReference: 'AAP 第7版 第7章/第11章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '选择安全的外出装备',
      oneLineSummary: '推车和婴儿安全座椅是出门标配，每次使用安全座椅都必须扣紧五点式安全带。',
      detailedExplanation:
        '1-3个月宝宝外出时需要使用适合其体重和身长的婴儿安全座椅（后向式）。关键安全步骤：每次都要扣紧五点式安全带（肩膀、腰、腿之间的所有卡扣），安全带应紧贴身体——以手指夹不住肩带为准。安全座椅的后向角度应在30°-45°，避免宝宝头前倾影响呼吸。冬季不要把穿着羽绒服的宝宝塞进安全座椅——厚衣服会形成空隙让安全带失效，应脱掉厚外套、扣好后再盖毯子。推车使用时始终踩刹车，手提婴儿提篮时确认与推车卡扣是否锁紧。',
      commonMisconception: '短途或者"就开几分钟"就不扣安全带，或者肩上安全带松松垮垮。',
      sourceReference: 'AAP 第7版 第11章/第15章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '何时可以开始趴着玩',
      oneLineSummary: '清醒时每天进行有监督的趴着玩（tummy time），从2-3次每次3-5分钟开始。',
      detailedExplanation:
        '趴着玩应在宝宝清醒且有成人全程监督的情况下进行，从出生后就可以开始。1-3个月阶段：每天进行2-3次趴着玩，每次3-5分钟逐渐增加，目标是到3个月时每天累计1小时。好处：锻炼颈部、肩部、背部和手臂肌肉，预防扁头，为翻身和坐立做准备。操作：在平坦硬实的地垫上进行，面前放一面镜子或黑白卡吸引注意力。如果宝宝烦躁拒绝，可以试试趴在照护者胸口、卷一条毛巾垫在腋下辅助。关键是耐心——许多宝宝一开始不喜欢趴着玩，从30秒、1分钟开始逐步延长。',
      commonMisconception: '宝宝一趴就哭就立刻抱起来，从此不再尝试。',
      sourceReference: 'AAP 第7版 第7章/第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡1：防跌落新挑战
  // ==========================================
  'stage1-safety-1': [
    {
      order: 1,
      title: '宝宝开始蹬腿移动，换尿布台是高危区域',
      oneLineSummary: '1-3个月宝宝虽然不会翻身，但会用腿蹬踹移动身体，永远不要在高处留宝宝无人看管。',
      detailedExplanation:
        '这个阶段的宝宝虽然还不能自主翻身，但腿部的蹬踹力量明显增大，可能在换尿布或穿衣时突然用腿蹬踹而移动身体。换尿布台、成人床、沙发等高处是最危险的跌落场景。铁律：任何时候在高处给宝宝换尿布或穿衣服时，一只手必须始终扶着宝宝。即使只是转身拿个尿布或湿巾，也要先把宝宝抱到安全地面。到2-3个月时，部分宝宝可能已经开始尝试翻身——第一次翻身往往在你没预料到的时候发生。',
      commonMisconception: '觉得"宝宝现在还不会翻身，我转身拿个东西没事"，结果就是一两秒的事。',
      sourceReference: 'AAP 第7版 第7章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '抱宝宝时防止自己摔倒',
      oneLineSummary: '抱宝宝走路时注意脚下和地面，湿滑地板、散落的物品、宠物都是摔倒隐患。',
      detailedExplanation:
        '1-3个月的宝宝还需要大量怀抱。抱宝宝时走路务必注意：①穿防滑拖鞋或赤脚，不要穿袜子在地板上走；②随时清理地面散落的玩具、充电线、杂志等；③如果家里有宠物，抱宝宝时注意宠物不要绊到脚；④夜间抱宝宝喂奶时留一盏小夜灯，避免摸黑走动；⑤如果实在太累或身体不适（如感冒头晕），考虑坐着喂而不是站着抱喂。照护者自身的疲劳也是摔倒的高危因素，家人的轮换很重要。',
      commonMisconception: '天天抱宝宝习惯了就放松警惕，一边看手机一边抱宝宝走路。',
      sourceReference: '梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '婴儿摇椅和弹跳椅的安全使用',
      oneLineSummary: '婴儿摇椅只能放在地面上用，绝对不能放在桌面、床上或沙发上。',
      detailedExplanation:
        '婴儿摇椅和弹跳椅可以让宝宝在清醒时安全地待一会儿，但必须遵守安全规则：①只能放在平坦的地面上——绝对不能放在桌子、床、沙发等高处，跌落的风险极高；②始终使用安全带；③不要提着摇椅移动（有些摇椅结构不支持）；④注意体重限制（通常上限在9kg左右），体重超出上限后继续使用可能结构不稳。更重要的原则：摇椅不是照护者离开时的替代——即使宝宝被安全地放在地面上的摇椅里，也不应长时间无人看管。',
      commonMisconception: '把摇椅放在餐桌上方便看着，或者放在床上软垫上觉得更舒服。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡2：居家环境安全升级
  // ==========================================
  'stage1-safety-2': [
    {
      order: 1,
      title: '检查婴儿床的安全性',
      oneLineSummary: '床栏间距不超过6厘米、床垫与床栏无缝隙、不安装床围、不放毛绒玩具或厚被子。',
      detailedExplanation:
        '到1-3个月阶段，应该全面检查一遍婴儿床的安全性：①床栏间距不应超过6厘米（约2.4英寸），防止宝宝头卡住；②床垫必须是硬的、紧密贴合床边，手指插不进缝隙；③不要安装床围——曾经有婴儿因脸贴在床围上窒息而死的报告，AAP已不推荐任何类型的床围；④床上只保留一件紧裹的床单和宝宝本人，没有任何枕头、被子、毛毯、毛绒玩具；⑤确认所有螺丝和连接件没有松动，床的油漆没有剥落。二手的婴儿床要特别注意检查是否满足当前安全标准。',
      commonMisconception: '为了好看买漂亮的床围和床品套装，装好后觉得没问题。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '预防宠物与宝宝的安全隐患',
      oneLineSummary: '宠物和宝宝在同一空间时，必须始终有成人直接监督，不能"相信"宠物的脾气。',
      detailedExplanation:
        '在1-3个月这个阶段，宝宝对宠物还没有任何防御能力——猫咪可能因温暖而趴到宝宝身上造成窒息，狗狗可能被哭声惊扰产生不可预测的反应。即使是最温顺的宠物，也永远不要让宠物和宝宝单独在同一个房间。具体做法：①抱着宝宝时不要让宠物直接凑近宝宝的脸；②在宝宝房间安装防护门栏，阻止宠物进入；③当宝宝在游戏垫上活动时，宠物必须在另一个房间或在成人直接监控下；④保持宠物疫苗接种和驱虫。',
      commonMisconception: '"我家狗/猫特别乖从没咬过人，没事的"——任何动物都可能被哭声吓到。',
      sourceReference: '梅奥育儿全书 第十九章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '避免二手烟和三手烟暴露',
      oneLineSummary: '二手烟增加SIDS风险，三手烟（残留在衣服头发家具上的烟草毒素）同样有害。',
      detailedExplanation:
        '烟草暴露是SIDS的可预防危险因素之一，可使SIDS风险增加2-3倍。二手烟：任何人在宝宝活动的室内空间吸烟都不行，包括其他房间——烟雾会通过门缝扩散。三手烟：尼古丁和致癌物会在吸烟者的衣服、头发、皮肤和家具表面残留数小时甚至数天。抱宝宝前：抽烟的家人应换掉吸烟时穿的外套、洗好手和脸。对来家里做客的亲友也要明确传达无烟环境的要求——这不是礼貌问题而是健康底线。',
      commonMisconception: '去阳台或室外抽完烟再进来就没事了，或者开个窗户抽就"不算二手烟"。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 安全模块 - 关卡3：窒息与烫伤预防升级
  // ==========================================
  'stage1-safety-3': [
    {
      order: 1,
      title: '抓握期的窒息新风险',
      oneLineSummary: '2-3个月宝宝开始有意识抓握物品并往嘴里送，所有直径小于4厘米的物品都要远离。',
      detailedExplanation:
        '1-3个月是一个重大转变期：宝宝从被动的看客变成了主动的探索者——约8周起，宝宝开始能伸手够东西并抓住，然后本能的反应就是把一切抓到的物品往嘴里塞。这意味着此前安全的物品现在都可能变成隐患。需要立即清理宝宝可触及范围内的所有小物品：硬币、纽扣电池（尤其危险——吞下后可能造成食道烧穿）、吊坠、药片、头绳、气球碎片、塑料袋等。一个实用检测方法：能穿过卫生纸卷筒中间孔洞的物品，对3岁以下儿童都属窒息风险品。',
      commonMisconception: '觉得宝宝还小"不可能够到"，沙发上随手放的硬币、药片没有收起来。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '烫伤预防——你没想到的隐患',
      oneLineSummary: '抱宝宝时不接触热饮和热食；洗澡水先放冷水再加热水；热水器设置调至49℃以下。',
      detailedExplanation:
        '对婴幼儿来说，烫伤是家庭最常见的意外伤害之一，而在1-3个月阶段，烫伤主要来自照护者的行为：①抱着宝宝喝热咖啡/热汤——宝宝突然头一撞，热液洒在宝宝脸上或身上；②用微波炉加热母乳或配方奶——微波加热不均匀形成"热点"，可能烫伤宝宝口腔；③洗澡时先放热水再放冷水，疏忽间热水已漫过宝宝脚。预防：永远不抱着宝宝接触任何热液；热奶用热水浴加热而非微波炉；洗澡先放冷水；将热水器温度调至49℃（120℉）以下，防止水龙头误开烫伤。',
      commonMisconception: '"我抱着宝宝喝了多少次咖啡都没事"——意外就发生在你以为没事的那一次。',
      sourceReference: 'AAP 第7版 第17章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '不要挂任何东西在宝宝脖子上',
      oneLineSummary: '长命锁、护身符、牙胶绳、安抚奶嘴挂绳都可能造成勒颈窒息，绝对禁止。',
      detailedExplanation:
        '在一些家庭传统中，会给宝宝戴长命锁、护身符或各种挂坠。现代育儿中，安抚奶嘴挂绳和牙胶挂绳也越来越常见。但任何挂在宝宝脖子上的东西都构成勒颈危险——宝宝可能被绳子缠住脖子，或者在婴儿床栏杆上挂住。FDA和美国消费者安全委员会多次发出警告，已有多起勒颈致死事件与此相关。如果使用安抚奶嘴，选择一体成型无挂绳的款式。任何传统饰品如果要佩戴，应夹在衣服上而非挂在脖子上。',
      commonMisconception: '长辈送的"平安"饰品不好意思不用，挂上觉得没事。',
      sourceReference: 'AAP 第7版 第15章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 发育模块 - 关卡1：运动发育里程碑
  // ==========================================
  'stage1-dev-1': [
    {
      order: 1,
      title: '抬头能力：从摇晃到稳定',
      oneLineSummary: '1个月仰卧转头，2个月俯卧抬头45°，3个月俯卧抬头90°且稳定，是经典发育轨迹。',
      detailedExplanation:
        '抬头是婴儿最早的大运动里程碑，1-3个月变化极快：①1个月：仰卧时可把头从一侧转到另一侧，抱起时头仍后垂，俯卧时可瞬间抬离床面；②2个月：俯卧时能抬头约45°，头部控制仍有摇晃但已明显有力；③3个月：俯卧时能抬头90°并保持稳定，用前臂支撑上身，抱起时头部不再后垂。帮助发育的最佳方法就是每天坚持趴着玩——这是0-6个月最重要的发育活动。如果宝宝到3个月俯卧时完全不能抬头，或在任何姿势下头部始终后垂，应让儿科医生评估。',
      commonMisconception: '宝宝不喜欢趴着玩就不做了，错过核心的颈部力量训练机会。',
      sourceReference: 'AAP 第7版 第6章/第7章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '翻身的准备信号（2-3个月）',
      oneLineSummary: '翻身通常在4-5个月出现，但2-3个月时左右扭动、侧翻倾向是翻身的准备信号。',
      detailedExplanation:
        '虽然大多数宝宝到4-5个月才会完成从仰卧到俯卧的完整翻身，但2-3个月时已经可以看到重要的准备迹象：①仰卧时频繁扭动身体、把腿踢得很高；②把双腿向一侧倾倒，身体顺势倾斜；③趴着玩时尝试用手臂推地面想翻过去。这些是核心力量和身体协调性发展的正常表现。安全提醒：当宝宝开始出现这些翻身迹象时，襁褓必须停止，也不要把宝宝单独放在沙发、成人床等任何高处——哪怕只是一瞬间。',
      commonMisconception: '看到宝宝往一边倒觉得"腿是不是歪了"，抱住不让动。',
      sourceReference: 'AAP 第7版 第6章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '从抓握反射到有意识的手部动作',
      oneLineSummary: '新生儿握拳反射消退后，宝宝2-3个月开始有意识地张开手、挥动双臂、尝试够东西。',
      detailedExplanation:
        '从新生儿到2-3个月，手部运动经历了一个从反射到有意识控制的戏剧性转变：①新生儿期双手紧握、拇指内收——这是握持反射，不是有意识的行为；②1-2个月时反射减弱，手指开始自然展开；③2-3个月时出现"发现双手"——宝宝会盯着自己的手看，双臂在面前挥舞，并开始有意识地把手往嘴里送。此时可以：给宝宝提供容易抓握的安全摇铃、在游戏垫上方悬挂轻便安全的玩具刺激够取行为。不要把宝宝的手套在袖子里或用防抓手套长时间束缚——自由的手和触觉体验对大脑发育至关重要。',
      commonMisconception: '一直给宝宝戴防抓手套怕抓脸，剥夺了手部探索和触觉发育的机会。',
      sourceReference: 'AAP 第7版 第6章/第7章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 4,
      title: '发育的警惕信号',
      oneLineSummary: '到3个月俯卧完全不能抬头、全身僵硬或松软、双手始终紧握不伸开，需及时评估。',
      detailedExplanation:
        '虽然个体差异很大，但以下表现需要儿科医生进一步评估：①到3个月俯卧时头部完全不能抬离床面，或抱起时头始终向后垂；②四肢异常僵硬（像棍子一样掰不动）或异常松软（像布娃娃一样没有张力）；③到3个月时双手仍然持续紧握，手指无法打开；④到2个月仍然不看人的脸、不追视移动物体；⑤听到很大的声音完全没有反应。这些信号的共同特征是"持续性"和"不对称性"——偶而出现不一定有问题，但持续每次都如此需要重视。早发现早干预效果最好。',
      commonMisconception: '觉得"每个孩子发育不一样，大点自然就会了"，延误早期干预的黄金窗口。',
      sourceReference: 'AAP 第7版 第6章/第7章',
      reviewStatus: 'draft',
    },
  ],
  // ==========================================
  // 1-3 个月 - 发育模块 - 关卡2：感官与社交发育
  // ==========================================
  'stage1-dev-2': [
    {
      order: 1,
      title: '视觉追踪能力的快速提升',
      oneLineSummary: '1个月追随人脸，2个月水平追视移动物，3个月可180°流畅追视并开始辨识颜色。',
      detailedExplanation:
        '视觉在0-3个月飞速发育：①1个月：能看20-30厘米距离（刚好是喂奶时看到妈妈脸的距离），喜欢看简单的高对比度图案（黑白卡）；②2个月：开始流畅地水平追视缓慢移动的物体，对人脸表现出明显偏好，会盯着看很久；③3个月：追视能力扩展到180°——物体从左到右全程跟随，开始对红、黄等鲜艳颜色有反应，视觉可以聚焦在更远的物体上。可以给宝宝看黑白卡片、在眼前缓慢移动玩具、面对面对话做表情——这些都是免费的"视觉早教"。',
      commonMisconception: '给宝宝买复杂的五颜六色电子玩具"开发视力"，忽略了最简单的面对面互动才是最好的刺激。',
      sourceReference: 'AAP 第7版 第6章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '听觉发育与声音回应',
      oneLineSummary: '宝宝从被动听到主动回应：1个月对响声惊跳，2个月会安静听人声，3个月转头找声源。',
      detailedExplanation:
        '听觉的发育轨迹：①1个月：完全能听到，对突然的大声响有惊跳反应（莫罗反射），听到熟悉的声音会平静；②2个月：对说话声表现出明显兴趣——会安静下来"听"你说话，可能会发出"咕咕"声回应；③3个月：能定向找声源——听到妈妈的说话声会转头去寻找，对熟悉的声音和陌生的声音有不同的反应。促进方法：多和宝宝说话、唱歌、读绘本——用真人声音而非电子播放器，真人声音能根据宝宝的反应调整音量和音调。检查宝宝是否对巨大声音完全没有反应，如有疑虑做听力筛查。',
      commonMisconception: '用手机播放儿歌代替自己唱，认为"宝宝听不懂说话也没意义"。',
      sourceReference: 'AAP 第7版 第6章',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '社交性微笑的到来！',
      oneLineSummary: '约6-8周时宝宝会出现"社交性微笑"——不是生理性反射而是有意识地对人笑，这是里程碑式的飞跃。',
      detailedExplanation:
        '新生儿时期偶尔在睡梦中露出的微笑是生理性反射，不代表情绪。真正的"社交性微笑"通常在6-8周出现——这是代表宝宝社会情感发育的巨大飞跃：当你对宝宝笑、说话、做鬼脸时，宝宝会看着你的脸，然后给你一个真正有意识的回应性微笑。这个阶段的宝宝开始表现出对人的强烈偏好——比起玩具，更喜欢看人脸。可以多和宝宝做"表情对话"：你笑我也笑，你做夸张表情我来回应。这不仅是亲子互动的甜蜜时刻，更是在建立情感连接和镜像神经元发育的基础。',
      commonMisconception: '宝宝回了微笑就高兴一下，没有意识到这是重要的发育里程碑，继续很少和宝宝互动。',
      sourceReference: 'AAP 第7版 第6章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
    {
      order: 4,
      title: '咿呀学语——最初的"对话"',
      oneLineSummary: '宝宝2个月左右开始发出"啊啊""咕咕"等元音，这是语言发育的起点，需要被听见并回应。',
      detailedExplanation:
        '从新生儿时期的哭声到2个月左右的"咕咕""啊啊"——这是宝宝进入了"cooing"（咕咕期）阶段，标志着发音器官和语言中枢的初步配合。宝宝在这个阶段会注意到自己发出的声音，并会因为发出声音得到回应而继续发。促进方法：①宝宝发出声音时，模仿他的声音回应——这叫"模仿对话"，是语言发展的黄金法则；②用略高的声调、拖长的音节和夸张的表情和宝宝说话——不是刻意"儿童化"，而是自然吸引宝宝注意；③轮流"对话"——你说、宝宝回应、你再回应。不需要任何高科技早教工具，你自己的声音和脸就是最好的语言老师。',
      commonMisconception: '觉得"这么点大能懂什么"，喂完奶放下就不管了，不跟宝宝对话互动。',
      sourceReference: 'AAP 第7版 第6章/第12章；梅奥育儿全书 第十八章',
      reviewStatus: 'draft',
    },
  ],

  'newborn-dev-2': [
    {
      order: 1,
      title: '生理性体重下降是正常的',
      oneLineSummary: '新生儿出生后前几天体重下降 7%-10% 属正常现象，约 7-10 天恢复出生体重。',
      detailedExplanation:
        '几乎所有新生儿在出生后前几天都会经历生理性体重下降，主要原因是体内多余水分的排出、胎便的排泄和暂时性的摄入不足。下降幅度通常在出生体重的 7%-10% 以内。足月健康新生儿一般在出生后 7-10 天恢复到出生体重。只要体重趋势在回升就无需过分焦虑。如果体重下降超过 10%，或出生 2 周后仍未恢复出生体重，应及时咨询医生。',
      commonMisconception: '看到体重下降就认为喂养不够，盲目增量或频繁更换奶粉品牌。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 2,
      title: '新生儿期正常的体重增长速度',
      oneLineSummary: '恢复出生体重后，新生儿前 3 个月通常每天增长 14-28 克，但存在个体差异。',
      detailedExplanation:
        '恢复出生体重后，新生儿在头 3 个月里平均每天增长约 14-28 克（每周约 100-200 克）。但这只是参考范围，每个宝宝的生长曲线不同。判断体重增长是否正常，重要的是看整体趋势而非单次称重数据——连续几次称重持续增长就是好的信号。定期使用婴儿专用体重秤、在相似条件下（如喂奶前、脱掉衣服和尿布）称重可以获得更准确的数据。具体评估应由儿科医生结合生长曲线图进行。',
      commonMisconception: '每天在家里用成人秤称宝宝体重，看到少了几十克就焦虑。',
      sourceReference: 'AAP 第7版 第3章/第6章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
    {
      order: 3,
      title: '体重增长不良的警示信号',
      oneLineSummary: '持续体重不增或下降、出生2周未恢复出生体重、尿量明显减少、精神差，都需要就医。',
      detailedExplanation:
        '需要关注的信号包括：出生超过 2 周仍低于出生体重；体重增长明显慢于标准的生长曲线百分位；体重持续下降而非增长；湿尿布数量明显减少（每天少于 4-6 片）；宝宝长期嗜睡、吸吮无力、清醒时间很少；喂养时容易疲劳、每次喂养时间过短。这些情况可能提示喂养不足、喂养困难或潜在的健康问题，应及时让儿科医生评估。不要等到体重明显落后了才行动。',
      commonMisconception: '觉得"宝宝瘦点没关系，大了自然会追上"，对早期的生长偏离不够重视。',
      sourceReference: 'AAP 第7版 第3章；梅奥育儿全书',
      reviewStatus: 'draft',
    },
  ],
};
